import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { SscIconComponent, SscIconHierarchy } from '../ssc-icon/ssc-icon.component';
import { TranslocoPipe } from '@ngneat/transloco';

@Component({
  selector: 'ssc-empty-placeholder',
  imports: [TranslocoPipe, SscIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="empty-placeholder__halo" aria-hidden="true"></div>

    <div class="empty-placeholder__icon-wrap">
      <div class="empty-placeholder__icon-ring" aria-hidden="true"></div>
      <div
        class="empty-placeholder__icon flex items-center justify-center
               h-20 w-20 rounded-3xl
               bg-primary-faint dark:bg-surface-dark
               border border-border-faint dark:border-border-dark
                shadow-sm">
        <ssc-icon
          class="-ml-2"
          [hierarchy]="iconHierarchy.basic"
          [svg]="icon()" />
      </div>
    </div>

    <div
      class="empty-placeholder__title text-lg font-medium text-center
                text-text-dark dark:text-text-faint">
      {{ title() | transloco }}
    </div>

    @if (resolvedMessage(); as msg) {
    <div
      class="empty-placeholder__message text-sm
             text-text-subtle dark:text-text-soft
             max-w-[200px] text-center">
      {{ msg | transloco }}
    </div>
    }

    <div class="mt-2">
      <ng-content></ng-content>
    </div>
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.75rem;
        padding: 2.5rem 1.5rem;
        position: relative;
        overflow: hidden;
      }

      .empty-placeholder__halo {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 280px;
        height: 280px;
        transform: translate(-50%, -55%);
        background: radial-gradient(
          circle at center,
          rgb(0 0 0 / 0.06) 0%,
          rgb(0 0 0 / 0) 65%
        );
        pointer-events: none;
        animation: empty-placeholder-pulse 3.6s ease-in-out infinite;
      }

      :host-context(.dark) .empty-placeholder__halo {
        background: radial-gradient(
          circle at center,
          rgb(255 255 255 / 0.07) 0%,
          rgb(255 255 255 / 0) 65%
        );
      }

      .empty-placeholder__icon-wrap {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        animation: empty-placeholder-float 4.2s ease-in-out infinite;
      }

      .empty-placeholder__icon-ring {
        position: absolute;
        inset: -8px;
        border-radius: 1.75rem;
        border: 1px solid currentColor;
        color: rgb(0 0 0 / 0.08);
        opacity: 0;
        animation: empty-placeholder-ring 2.8s ease-out infinite;
      }

      :host-context(.dark) .empty-placeholder__icon-ring {
        color: rgb(255 255 255 / 0.12);
      }

      .empty-placeholder__icon {
        animation: empty-placeholder-icon-in 520ms
          cubic-bezier(0.22, 1, 0.36, 1) both;
      }

      .empty-placeholder__title {
        animation: empty-placeholder-rise 520ms cubic-bezier(0.22, 1, 0.36, 1)
          120ms both;
      }

      .empty-placeholder__message {
        animation: empty-placeholder-rise 520ms cubic-bezier(0.22, 1, 0.36, 1)
          200ms both;
      }

      ::ng-deep .empty-placeholder__icon i {
        animation: empty-placeholder-icon-tilt 4.2s ease-in-out infinite;
        transform-origin: 50% 60%;
        display: inline-block;
      }

      @keyframes empty-placeholder-float {
        0%,
        100% {
          transform: translateY(0);
        }
        50% {
          transform: translateY(-6px);
        }
      }

      @keyframes empty-placeholder-icon-tilt {
        0%,
        100% {
          transform: rotate(-3deg);
        }
        50% {
          transform: rotate(3deg);
        }
      }

      @keyframes empty-placeholder-pulse {
        0%,
        100% {
          opacity: 0.55;
          transform: translate(-50%, -55%) scale(0.96);
        }
        50% {
          opacity: 1;
          transform: translate(-50%, -55%) scale(1.04);
        }
      }

      @keyframes empty-placeholder-ring {
        0% {
          opacity: 0.6;
          transform: scale(0.85);
        }
        80% {
          opacity: 0;
          transform: scale(1.25);
        }
        100% {
          opacity: 0;
          transform: scale(1.25);
        }
      }

      @keyframes empty-placeholder-icon-in {
        0% {
          opacity: 0;
          transform: scale(0.8) translateY(8px);
        }
        100% {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }

      @keyframes empty-placeholder-rise {
        0% {
          opacity: 0;
          transform: translateY(8px);
        }
        100% {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .empty-placeholder__halo,
        .empty-placeholder__icon-wrap,
        .empty-placeholder__icon-ring,
        .empty-placeholder__icon,
        .empty-placeholder__title,
        .empty-placeholder__message,
        ::ng-deep .empty-placeholder__icon i {
          animation: none;
        }
      }
    `,
  ],
})
export class SscEmptyPlaceholderComponent {
  title = input<string>('list|empty|title');
  message = input<string | null>('list|empty|message');
  icon = input<string>('list-magnifying-glass');

  protected iconHierarchy = SscIconHierarchy;
  protected resolvedMessage = computed(() => this.message() || null);
}
